import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, IconButton, Button,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Card, CardContent, Tooltip, InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Refresh as RefreshIcon, Close as CloseIcon, Search as SearchIcon,
  Discount as DiscountIcon, CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';

import PatientAutocomplete from '../../components/PatientAutocomplete';

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

interface PendingDiscount {
  id: string;
  patient_id: string | null;
  patient_name: string;
  original_amount: number;
  discount_type: 'percentage' | 'fixed';
  discount_percentage: number;
  discount_amount: number;
  reason: string;
  valid_until: string | null;
  status: string;
  transaction_id: string | null;
  budget_id: string | null;
  notes: string;
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'pending', label: 'Pendente' },
  { value: 'applied', label: 'Aplicado' },
  { value: 'expired', label: 'Expirado' },
  { value: 'cancelled', label: 'Cancelado' },
];

const statusColor = (s: string): 'warning' | 'success' | 'default' | 'error' => {
  switch (s) {
    case 'pending': return 'warning';
    case 'applied': return 'success';
    case 'expired': return 'default';
    case 'cancelled': return 'error';
    default: return 'default';
  }
};

const statusLabel = (s: string) => {
  switch (s) {
    case 'pending': return 'Pendente';
    case 'applied': return 'Aplicado';
    case 'expired': return 'Expirado';
    case 'cancelled': return 'Cancelado';
    default: return s;
  }
};

const emptyForm = {
  patient_id: '',
  original_amount: '',
  discount_type: 'percentage' as 'percentage' | 'fixed',
  discount_percentage: '',
  discount_amount: '',
  reason: '',
  valid_until: '',
  transaction_id: '',
  budget_id: '',
  notes: '',
};

const PendingDiscounts: React.FC = () => {
  const { isMobile } = usePageBreakpoints();

  const [rows, setRows] = useState<PendingDiscount[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPendingAmount, setTotalPendingAmount] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof emptyForm>({ ...emptyForm });
  const [selectedPatient, setSelectedPatient] = useState<{ id: string; full_name: string; cpf?: string; phone?: string; email?: string } | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page: page + 1, limit };
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterSearch) params.patient_id = filterSearch;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;

      const { data } = await axios.get(getApiUrl('/financial-module/pending-discounts'), { params });
      const payload = data.data || data;
      setRows(payload.rows || payload.items || payload || []);
      setTotal(payload.total || 0);
      setTotalPendingAmount(payload.total_pending_amount || 0);
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao carregar descontos', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [page, limit]);

  const handleFilter = () => { setPage(0); loadData(); };

  const handleFormChange = (field: string, value: string) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'discount_type') {
        updated.discount_percentage = '';
        updated.discount_amount = '';
      }
      if (field === 'discount_percentage' && updated.discount_type === 'percentage') {
        const orig = parseFloat(updated.original_amount) || 0;
        const pct = parseFloat(value) || 0;
        updated.discount_amount = ((orig * pct) / 100).toFixed(2);
      }
      if (field === 'original_amount' && updated.discount_type === 'percentage') {
        const orig = parseFloat(value) || 0;
        const pct = parseFloat(updated.discount_percentage) || 0;
        updated.discount_amount = ((orig * pct) / 100).toFixed(2);
      }
      return updated;
    });
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setSelectedPatient(null);
    setOpenDialog(true);
  };

  const openEdit = (row: PendingDiscount) => {
    setEditingId(row.id);
    setForm({
      patient_id: row.patient_id || '',
      original_amount: String(row.original_amount || ''),
      discount_type: row.discount_type,
      discount_percentage: String(row.discount_percentage || ''),
      discount_amount: String(row.discount_amount || ''),
      reason: row.reason || '',
      valid_until: row.valid_until ? row.valid_until.slice(0, 10) : '',
      transaction_id: row.transaction_id || '',
      budget_id: row.budget_id || '',
      notes: row.notes || '',
    });
    setSelectedPatient(
      row.patient_id ? { id: row.patient_id, full_name: row.patient_name || '' } : null
    );
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      const body = {
        ...form,
        original_amount: parseFloat(form.original_amount) || 0,
        discount_percentage: form.discount_type === 'percentage' ? (parseFloat(form.discount_percentage) || 0) : null,
        discount_amount: parseFloat(form.discount_amount) || 0,
        patient_id: selectedPatient?.id || null,
        transaction_id: form.transaction_id || null,
        budget_id: form.budget_id || null,
      };

      if (editingId) {
        await axios.put(getApiUrl(`/financial-module/pending-discounts/${editingId}`), body);
        setSnackbar({ open: true, message: 'Desconto atualizado!', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/financial-module/pending-discounts'), body);
        setSnackbar({ open: true, message: 'Desconto criado!', severity: 'success' });
      }
      setOpenDialog(false);
      loadData();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao salvar', severity: 'error' });
    }
  };

  const handleApply = async (id: string) => {
    try {
      await axios.post(getApiUrl(`/financial-module/pending-discounts/${id}/apply`));
      setSnackbar({ open: true, message: 'Desconto aplicado com sucesso!', severity: 'success' });
      loadData();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao aplicar desconto', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Deseja excluir este desconto?')) return;
    try {
      await axios.delete(getApiUrl(`/financial-module/pending-discounts/${id}`));
      setSnackbar({ open: true, message: 'Excluído com sucesso!', severity: 'success' });
      loadData();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao excluir', severity: 'error' });
    }
  };

  return (
    <Box sx={pageContainerSx}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DiscountIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h5" fontWeight={700}>Descontos Pendentes</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData}>Atualizar</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Novo Desconto</Button>
        </Box>
      </Box>

      {/* Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ backgroundColor: '#fff3e0' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary">Total Descontos Pendentes</Typography>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#e65100' }}>
                {formatCurrency(totalPendingAmount)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
                {STATUS_OPTIONS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Buscar (Paciente ID)" value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" type="date" label="Data Início" value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" type="date" label="Data Fim" value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button fullWidth variant="contained" onClick={handleFilter}>Filtrar</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: '#f5f5f5' } }}>
                  <TableCell>Paciente</TableCell>
                  <TableCell align="right">Valor Original</TableCell>
                  <TableCell align="right">Desconto</TableCell>
                  <TableCell align="center">Tipo</TableCell>
                  <TableCell>Motivo</TableCell>
                  <TableCell>Validade</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">Nenhum desconto encontrado</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{(r as any).clinic_patients?.full_name || r.patient_name || '-'}</TableCell>
                      <TableCell align="right">{formatCurrency(r.original_amount)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(r.discount_amount)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={r.discount_type === 'percentage' ? 'Percentual' : 'Fixo'}
                          size="small"
                          variant="outlined"
                          color={r.discount_type === 'percentage' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.reason || '-'}
                      </TableCell>
                      <TableCell>{r.valid_until ? new Date(r.valid_until).toLocaleDateString('pt-BR') : '-'}</TableCell>
                      <TableCell align="center">
                        <Chip label={statusLabel(r.status)} color={statusColor(r.status)} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          {r.status === 'pending' && (
                            <Tooltip title="Aplicar Desconto">
                              <IconButton size="small" color="success" onClick={() => handleApply(r.id)}>
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Editar">
                            <IconButton size="small" color="primary" onClick={() => openEdit(r)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={limit}
          onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Linhas por página"
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog fullScreen={isMobile} scroll="paper" open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editingId ? 'Editar Desconto' : 'Novo Desconto'}
          <IconButton onClick={() => setOpenDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <PatientAutocomplete
                value={selectedPatient}
                onChange={(p) => {
                  setSelectedPatient(p);
                  setForm((prev) => ({ ...prev, patient_id: p?.id || '' }));
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Valor Original" type="number" value={form.original_amount}
                onChange={(e) => handleFormChange('original_amount', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo Desconto</InputLabel>
                <Select value={form.discount_type} label="Tipo Desconto" onChange={(e) => handleFormChange('discount_type', e.target.value)}>
                  <MenuItem value="percentage">Percentual</MenuItem>
                  <MenuItem value="fixed">Fixo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {form.discount_type === 'percentage' ? (
              <>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Percentual" type="number" value={form.discount_percentage}
                    onChange={(e) => handleFormChange('discount_percentage', e.target.value)}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="Valor Desconto" type="number" value={form.discount_amount} disabled
                    InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Valor Desconto" type="number" value={form.discount_amount}
                  onChange={(e) => handleFormChange('discount_amount', e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Motivo" value={form.reason}
                onChange={(e) => handleFormChange('reason', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="date" label="Validade" value={form.valid_until}
                onChange={(e) => handleFormChange('valid_until', e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="ID Transação" value={form.transaction_id}
                onChange={(e) => handleFormChange('transaction_id', e.target.value)} placeholder="Opcional" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="ID Orçamento" value={form.budget_id}
                onChange={(e) => handleFormChange('budget_id', e.target.value)} placeholder="Opcional" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Observações" multiline rows={2} value={form.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.original_amount || !form.discount_amount}>
            {editingId ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PendingDiscounts;
